<script setup>
import { computed, ref, onMounted } from 'vue'
import { Box, Typography, Icon, TextButton } from '@aryeo/runway'
import useLocalStorage from '@/Hooks/Shared/useLocalStorage.js'

const props = defineProps({
  description: {
    type: String,
    required: true,
  },
  variant: {
    type: String,
    default: 'info',
    validator: (value) => ['info', 'warning', 'danger', 'primary'].includes(value),
  },
  identifier: {
    type: String,
  },
  isDismissible: {
    type: Boolean,
    default: false,
  },
})

const dismissed = ref(false)

const alertRole = computed(() => (props.variant === 'danger' ? 'alert' : 'status'))

const { getItem, setItem } = useLocalStorage()

onMounted(() => {
  if (props.identifier && props.isDismissible && getItem(`dismissed.${props.identifier}`)) {
    dismissed.value = true
  }
})

const dismiss = () => {
  if (props.identifier && props.isDismissible) {
    dismissed.value = true
    setItem(`dismissed.${props.identifier}`, true)
  }
}
const backgroundColor = computed(() => {
  switch (props.variant) {
    case 'info':
      return 'background-light'
    case 'warning':
      return 'black'
    case 'danger':
      return 'black'
    case 'primary':
      return 'primary'
    default:
      return 'background-light'
  }
})

const textColor = computed(() => {
  if (props.variant === 'info') {
    return 'body'
  }

  return 'white'
})

const icon = computed(() => {
  switch (props.variant) {
    case 'info':
      return 'infoCircle'
    case 'warning':
      return 'exclamationTriangle'
    case 'danger':
      return 'exclamationTriangle'
    case 'primary':
      return null
    default:
      return null
  }
})

const iconColor = computed(() => {
  switch (props.variant) {
    case 'info':
      return 'info'
    case 'warning':
      return 'warning'
    case 'danger':
      return 'danger'
    case 'primary':
      return 'white'
    default:
      return 'info'
  }
})
</script>
<template>
  <Box
    v-if="!dismissed"
    paddingX="small"
    paddingY="xsmall"
    :backgroundColor="backgroundColor"
    :role="alertRole"
  >
    <div class="flex justify-between max-w-5xl mx-auto">
      <div class="flex items-center gap-2">
        <Icon v-if="icon" :name="icon" :color="iconColor" size="small" />
        <Typography :color="textColor" variant="body-sm">{{ description }}</Typography>
      </div>
      <div class="flex items-center gap-2">
        <slot name="actions" />
        <TextButton
          v-if="isDismissible"
          iconName="timesCircle"
          :color="textColor"
          size="small"
          aria-label="Dismiss alert"
          title="Dismiss alert"
          @click="dismiss"
        />
      </div>
    </div>
  </Box>
</template>
